import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { api } from '../utils/api';
import { motion } from 'framer-motion';
import { Play, Lock, Video as VideoIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function VideosPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const response = await api.getVideos();
      setVideos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar vídeos');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = (video) => {
    if (video.plan_required === 'PREMIUM' && user?.plan !== 'PREMIUM') {
      toast.error('Este vídeo é exclusivo para assinantes Premium');
      return;
    }
    setSelectedVideo(video);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-4 md:p-8 max-w-7xl mx-auto"
      >
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Vídeos Educacionais
          </h1>
          <p className="text-muted-foreground text-lg">Aprenda com conteúdo de qualidade</p>
        </div>

        {selectedVideo && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 rounded-2xl overflow-hidden bg-black"
            data-testid="video-player"
          >
            <div className="aspect-video">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}`}
                title={selectedVideo.titulo}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="p-6 bg-white">
              <h2 className="text-2xl font-bold mb-2">{selectedVideo.titulo}</h2>
              <p className="text-muted-foreground">{selectedVideo.descricao}</p>
            </div>
          </motion.div>
        )}

        {videos.length === 0 ? (
          <div className="text-center py-20">
            <VideoIcon className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Nenhum vídeo disponível</h3>
            <p className="text-muted-foreground">Os vídeos estarão disponíveis em breve!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleVideoClick(video)}
                data-testid={`video-card-${index}`}
                className="video-card rounded-2xl overflow-hidden bg-white border border-border cursor-pointer"
              >
                <div className="relative aspect-video bg-muted">
                  <img
                    src={video.thumbnail || `https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`}
                    alt={video.titulo}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/40 transition-all">
                    {video.plan_required === 'PREMIUM' && user?.plan !== 'PREMIUM' ? (
                      <div className="h-16 w-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center">
                        <Lock className="h-8 w-8 text-primary" />
                      </div>
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform">
                        <Play className="h-8 w-8 text-primary ml-1" />
                      </div>
                    )}
                  </div>
                  {video.plan_required === 'PREMIUM' && (
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-gradient-to-r from-primary to-secondary text-white text-xs font-semibold">
                      PREMIUM
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-2 line-clamp-2">{video.titulo}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{video.descricao}</p>
                  {video.categoria && (
                    <div className="mt-3">
                      <span className="inline-block px-3 py-1 rounded-full bg-muted text-xs font-medium">
                        {video.categoria}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </Layout>
  );
}
